import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/currency";
import { toast } from "sonner";

export const CartSheet = () => {
  const { items, open, setOpen, updateQty, removeItem, clearCart, totals } = useCart();

  const checkout = () => {
    if (items.length === 0) return;
    toast.success("Order placed! We'll email you the details.", {
      description: `${totals.count} items · ${inr(totals.subtotal)}`,
    });
    clearCart();
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-sheet"
        side="right"
        className="w-full sm:max-w-md flex flex-col bg-background"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-3xl">Your bag</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4">
          {items.length === 0 ? (
            <div
              data-testid="empty-cart"
              className="h-full flex flex-col items-center justify-center text-center gap-3 py-16"
            >
              <ShoppingBag className="w-12 h-12 text-muted-foreground" strokeWidth={1.2} />
              <p className="font-display text-xl">Your bag is quiet.</p>
              <p className="text-sm text-muted-foreground max-w-[220px]">
                Add a jar of ghee, a bunch of carrots, a bottle of cold-pressed oil.
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="flex gap-4 pb-4 border-b border-border"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 rounded-lg object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base font-semibold leading-tight">
                      {item.name}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono-ui uppercase tracking-wider mt-1">
                      {item.unit}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-border rounded-full">
                        <button
                          data-testid={`decrement-${item.id}`}
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded-l-full"
                          aria-label="Decrease"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span
                          data-testid={`qty-${item.id}`}
                          className="w-8 text-center text-sm font-mono-ui"
                        >
                          {item.qty}
                        </span>
                        <button
                          data-testid={`increment-${item.id}`}
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded-r-full"
                          aria-label="Increase"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-sm font-mono-ui">
                        {inr(item.price * item.qty)}
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-${item.id}`}
                    onClick={() => removeItem(item.id)}
                    className="p-1 h-fit text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground uppercase tracking-widest font-mono-ui">
                Subtotal
              </span>
              <span
                data-testid="cart-subtotal"
                className="font-display text-2xl font-semibold"
              >
                {inr(totals.subtotal)}
              </span>
            </div>
            <Button
              data-testid="checkout-btn"
              onClick={checkout}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
            >
              Complete order · {inr(totals.subtotal)}
            </Button>
            <button
              onClick={clearCart}
              data-testid="clear-cart-btn"
              className="w-full text-xs text-muted-foreground hover:text-destructive underline underline-offset-4 py-1"
            >
              Empty bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
